import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardSubtitle,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonText,
    IonTitle,
    IonToolbar,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { GarminSDK } from "capacitor-garmin-sdk";
import { tokenMessage } from "../../services/watch/messages/getToken";
import { uuid } from "short-uuid";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
    setGarminKnownDevices,
    setGarminSelectedDevice,
} from "../../redux/actions/garminActions";
import { PluginListenerHandle } from "@capacitor/core/types/definitions";
import _ from "lodash";
import { isIos } from "../../services/platformDetection";
import { openStoreForConnectMobile } from "../../services/watch/GarminWatchHelper";
import { IQApp } from "capacitor-garmin-sdk";

type MessageKey =
    | "tokenResponse"
    | "configurationResponse"
    | "matchSummaryResponse"
    | "matchDetailsResponse";

const GARMIN_APP_IDS = {
    REFSIX_LITE: "5d5b8e3c-2fbb-48b6-bb23-bde871897730", // REFSIX Lite
    GARMIN_TEST_APP: "a3421fee-d289-106a-538c-b9547ab12095", // Garmin Test App
};

// Garmin full app
export const GARMIN_FULL_APP_INFO: IQApp = {
    uuid: "cbd9a3bd-58c9-4add-9a2e-00ab4ee2a4e8", // Scorza Garmin app
    storeUUID: "fcabde94-66ff-442c-bbc1-de04d0d0837f",
};

export const GarminDebugger: FC = () => {
    const { t } = useTranslation();
    // Default selected APP_ID is the REFSIX Full one
    const [selectedAppId, setSelectedAppId] = useState<string>(
        GARMIN_FULL_APP_INFO.uuid
    );
    const app = useMemo(() => {
        return {
            uuid: selectedAppId,
            storeUUID: GARMIN_FULL_APP_INFO.storeUUID,
        };
    }, [selectedAppId]);
    const knownDevices = useSelector(
        (state: RootState) => state.garminWatches?.knownDevices
    );
    const selectedDevice = useSelector(
        (state: RootState) => state.garminWatches?.selectedDevice
    );
    const dispatch = useDispatch();
    const [messageStatus, setMessageStatus] = useState<string | null>(null);
    const [deviceStatus, setDeviceStatus] = useState<string | null>(null);
    const [appStatusMessage, setAppStatusMessage] = useState<string | null>(
        null
    ); // For open app on watch status
    const [messagesFromWatch, setMessagesFromWatch] = useState<any[]>([]);
    const [previousDeviceId, setPreviousDeviceId] = useState<string | null>(
        null
    );
    const [buttonText, setButtonText] = useState<string>("Check App Info"); // State to hold the button text
    const [selectedMessage, setSelectedMessage] =
        useState<MessageKey>("tokenResponse");
    const [messageUuid, setMessageUuid] = useState<string>("1234");
    const [garminAppInstalled, setGarminAppInstalled] =
        useState<boolean>(false);
    let messageListener: PluginListenerHandle | undefined;
    let deviceStatusListener: PluginListenerHandle | undefined;

    const messages: Record<Partial<MessageKey>, any> = {
        configurationResponse: {
            configuration: {
                templateDoc: {
                    templates: [
                        {
                            name: "League",
                            config: {
                                timings: {
                                    period1: 45,
                                    period2: 45,
                                    interval1: 15,
                                },
                                periodsNo: "2",
                                withGoalScorers: false,
                                teamSize: 11,
                                subsNo: 5,
                                extraTimeAvailable: false,
                                penaltiesAvailable: false,
                                sinBinSystem: "none",
                            },
                            _id: "afd5b8db-838f-41b3-ba5f-0bfcd51fba7e",
                        },
                        {
                            name: "Cup",
                            config: {
                                timings: {
                                    period1: 45,
                                    period2: 45,
                                    interval1: 15,
                                    extraTimeHalfLength: 15,
                                },
                                periodsNo: "2",
                                withGoalScorers: false,
                                teamSize: 11,
                                subsNo: 5,
                                extraTimeAvailable: true,
                                penaltiesAvailable: true,
                                sinBinSystem: "none",
                            },
                            _id: "5fc6409e-ca6e-40a2-bba1-5b36c8db1b67",
                        },
                    ],
                    defaultTemplate: "afd5b8db-838f-41b3-ba5f-0bfcd51fba7e",
                },
                settings: {
                    disableWebPairing: false,
                    misconductCodes: [],
                    disableGPS: false,
                    playerNumberPicker: false,
                    showGoalOptions: false,
                    hide13: false,
                    recordRefereeRoles: false,
                    layout: {
                        main: "countdown",
                        left: "elapsed",
                        right: "additional",
                    },
                    recordGoalScorers: false,
                    automaticallyBeginHalfTime: true,
                    elapsedHalfTimerFromZero: false,
                    useImperial: false,
                    use12HourTime: false,
                    watchFace: "bigTimers",
                    misconductCode: "fifa",
                },
            },
        },
        matchSummaryResponse: {
            // matchSummary JSON goes here
        },
        matchDetailsResponse: {
            // matchDetails JSON goes here
        },
        tokenResponse: undefined,
    };

    const handleAppIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedAppId(e.target.value);
    };

    interface TokenPayload {
        token: {
            authTokenCreation: number;
            authTokenExpiry: number;
            authToken: string;
        };
    }

    const sendMessage = useCallback(async () => {
        if (!selectedDevice) {
            console.error("No selected device");
            return;
        }

        try {
            let payload = messages[selectedMessage];
            if (selectedMessage === "tokenResponse") {
                // Call the tokenMessage function and serialize the token field
                const tokenPayload = (await tokenMessage({
                    id: messageUuid,
                })) as TokenPayload;
                const newUuid = uuid();

                // Create a new payload object that contains the expected structure
                payload = {
                    requestId: messageUuid,
                    messageId: "tokenResponse",
                    id: newUuid,
                    _id: newUuid,
                    token: tokenPayload.token.authToken,
                };
            }
            console.log(
                "Sending message with payload: ",
                JSON.stringify(payload)
            );
            const request = {
                device: selectedDevice,
                app: {
                    uuid: selectedAppId,
                    storeUUID: GARMIN_FULL_APP_INFO.storeUUID,
                },
                payload: payload,
            };
            const result = await GarminSDK.sendMessageToWatch(request);

            // Log the entire result to see its structure
            console.log(
                "Send message result:",
                JSON.stringify(result, null, 4)
            );

            // Check for success property or status, as `sent` might not exist
            if (result && result.status === "success") {
                setMessageStatus(result.message);
                console.log(result.message);
            } else {
                setMessageStatus(result.message);
                console.log(result.message);
            }
        } catch (error) {
            if (error instanceof Error) {
                setMessageStatus(`Failed to send message: ${error.message}`);
                console.error(`Failed to send message: ${error.message}`);
            } else {
                setMessageStatus(
                    "Failed to send message: Unknown error occurred"
                );
                console.error("Failed to send message: Unknown error occurred");
            }
        }
    }, [selectedDevice, selectedMessage, messageUuid, selectedAppId, messages]);

    const unregisterForEvents = async (uuid: string) => {
        if (!selectedDevice) {
            setAppStatusMessage("No device selected");
            return;
        }
        // try {
        //     await GarminSDK.stopListeningToDevice({
        //         device: selectedDevice, // Pass the existing selectedDevice object
        //         app: app,
        //     });
        //     console.log(`Unregistered for app events on device ${uuid}`);
        // } catch (error) {
        //     console.error(`Failed to unregister for app events: ${error}`);
        // }
    };
    //
    // useEffect(() => {
    //     if (messageUuid) {
    //         console.log(`messageUuid: ${messageUuid}`);
    //         sendMessage().then((r) => {
    //             console.log("Message sent." + r);
    //         }); // Call sendMessage when messageUuid updates
    //     }
    // }, [messageUuid]);

    useEffect(() => {
        // @ts-ignore
        // Attach Garmin SDK to the window for debugging
        window.garmin = GarminSDK;
        console.log("[GarminDebugger] try and get known devices");

        GarminSDK.checkGarminConnectInstalled().then(async (result) => {
            if (!result.installed) {
                setGarminAppInstalled(false);
                console.log(
                    `[GarminDebugger] Not getting devices as app Connect app not installed`
                );
                return;
            }
            setGarminAppInstalled(true);
            getKnownDevices()
                .then((devices) => {
                    console.log("[GarminDebugger] Known devices:", devices);
                    if (devices && devices.devices) {
                        console.log(
                            `[GarminDebugger] Setting selected device to first device. ${devices.devices[0].friendlyName}`
                        );
                        // dispatch(setGarminSelectedDevice(devices.devices[0]));
                    }
                })
                .catch((e) => {
                    console.error(
                        "[GarminDebugger] Error fetching known devices",
                        e
                    );
                });
        });
    }, []);

    useEffect(() => {
        if (!selectedDevice) {
            return;
        }
        console.log("Started listening for messages from Garmin Watch.");

        // Unregister from previous device if exists
        if (previousDeviceId) {
            unregisterForEvents(previousDeviceId).then((r) => {
                console.log("Unregistered for events." + r);
            });
        }

        const messageListener = (message: any) => {
            console.log(`Message received: ${JSON.stringify(message)}`);
            setMessagesFromWatch((prevMessages) =>
                _.uniq([message, ...prevMessages])
            );

            // Check if the messageId is 'tokenRequest'
            if (message?.messageId === "tokenRequest") {
                console.log("Received tokenRequest. Sending tokenResponse...");
                if (typeof message?.id === "string") {
                    console.log("Received message ID:", message.id);
                    setMessageUuid(message.id);
                }
            }
        };

        const deviceStatusListener = (statusInfo: any) => {
            const { uuid, status } = statusInfo;
            console.log(`Device status changed for device ${uuid}: ${status}`);

            switch (status) {
                case 0: // IQDeviceStatus_InvalidDevice
                    console.log(`Device ${uuid} is invalid.`);
                    break;
                case 1: // IQDeviceStatus_BluetoothNotReady
                    console.log(`Bluetooth is not ready for device ${uuid}.`);
                    break;
                case 2: // IQDeviceStatus_NotFound
                    console.log(`Device ${uuid} not found.`);
                    break;
                case 3: // IQDeviceStatus_NotConnected
                    console.log(`Device ${uuid} is not connected.`);
                    break;
                case 4: // IQDeviceStatus_Connected
                    console.log(`Device ${uuid} is connected.`);
                    break;
                default:
                    console.log(`Unknown status for device ${uuid}: ${status}`);
                    break;
            }

            // Optionally, update your UI or state here based on the device status
        };

        // Clean up old listeners
        removeListeners()
            .then(() => {
                // Start listening on the newly selected device
                // GarminSDK.startListeningToDevice({
                //     device: selectedDevice, // Pass the existing selectedDevice object
                //     app: app,
                // });

                // Add the listener for received messages
                GarminSDK.addListener("messageReceived", messageListener).then(
                    (r) => {
                        console.log(
                            "Added listener for receiving messages." + r
                        );
                    }
                );
                // Add the listener for device status changes
                GarminSDK.addListener(
                    "deviceStatusChanged",
                    deviceStatusListener
                ).then((r) => {
                    console.log(
                        "Added listener for device status changes." + r
                    );
                });
                // Update previous device ID to the newly selected one
                setPreviousDeviceId(selectedDevice.uuid);
            })
            .catch((e) => {
                console.error("Error removing listeners", e);
            });

        return () => {
            console.log("Stopped listening to Garmin Watch.");
            removeListeners().catch(() => {}); // Cleans up all listeners, including 'messageReceived'
        };
    }, [
        previousDeviceId,
        selectedDevice,
        selectedAppId,
        app,
        unregisterForEvents,
    ]);

    const getKnownDevices = async () => {
        try {
            const devices = await GarminSDK.getKnownDevices();

            if (devices && devices.devices) {
                console.log("[GarminDebugger] Known devices:", devices.devices);
                dispatch(setGarminKnownDevices(devices.devices)); // Set known devices if they exist
            } else {
                console.warn(
                    "[GarminDebugger] No devices found or devices is null"
                );
                dispatch(setGarminKnownDevices([])); // Handle case where no devices are returned
            }

            return devices;
        } catch (error) {
            console.error(
                "[GarminDebugger] Error fetching known devices:",
                error
            );
            dispatch(setGarminKnownDevices([])); // Optionally set an empty array if there was an error
            return null; // Return null to indicate failure
        }
    };

    const getDeviceStatus = async () => {
        if (!selectedDevice) {
            console.error("No selected device");
            return;
        }
        try {
            const result = await GarminSDK.getDeviceStatus({
                device: selectedDevice,
            });
            console.log(
                "Get device status result:",
                JSON.stringify(result, null, 4)
            );
            setDeviceStatus(JSON.stringify(result, null, 2));
            console.log(result.status);
        } catch (error) {
            setDeviceStatus(JSON.stringify(error, null, 2));
        }
    };

    const checkAppInfo = async () => {
        if (!selectedDevice) {
            console.error("Garmin: No selected device");
            return;
        }
        try {
            const result = await GarminSDK.checkApplicationInfo({
                device: selectedDevice, // Pass the existing selectedDevice object
                app: app,
            });

            setButtonText(JSON.stringify(result, null, 2)); // Set button text to "Checking..." while checking
        } catch (error) {
            console.error("Error checking app info:", error);
            // setButtonText("Error occurred");
            setButtonText(JSON.stringify(error, null, 2));
        }
    };

    const openAppOnWatch = async () => {
        if (!selectedDevice) {
            setAppStatusMessage("No device selected");
            return;
        }

        try {
            const result = await GarminSDK.openGarminApp({
                device: selectedDevice, // Pass the existing selectedDevice object
                app: app,
            });
            console.log("Open app result:", JSON.stringify(result));
            if (result.status === "success") {
                setAppStatusMessage(result.message);
            } else {
                setAppStatusMessage(result.message);
            }
        } catch (error) {
            if (error instanceof Error) {
                setAppStatusMessage(`Failed to open app: ${error.message}`);
            } else {
                setAppStatusMessage(
                    "Failed to open app: Unknown error occurred"
                );
            }
        }
    };

    // Remove listener for the previous device
    const removeListeners = async () => {
        try {
            if (messageListener) {
                await messageListener.remove();
                messageListener = undefined;
            }
            if (deviceStatusListener) {
                await deviceStatusListener.remove();
                deviceStatusListener = undefined;
            }
        } catch (error) {
            console.error("[GarminDebugger] Error removing listeners:", error);
        }
    };

    // Define the function to open the store
    const openGarminStore = async () => {
        if (!selectedDevice) {
            console.error("No selected device");
            return;
        }
        try {
            const result = await GarminSDK.showConnectIQStoreForApp({
                device: selectedDevice, // Pass the existing selectedDevice object
                app: app,
            });
            console.log("Opened Garmin Connect IQ store.", result.status);
        } catch (error) {
            console.error("Error opening Garmin store:", error);
        }
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton
                            defaultHref="/"
                            text={t("help.OnboardingVideo.controls.back")}
                        />
                    </IonButtons>
                    <IonTitle>Garmin Debugger</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                {!garminAppInstalled && (
                    <IonCard>
                        <IonCardHeader>
                            <IonCardTitle>
                                Connect Mobile App Not Istalled
                            </IonCardTitle>
                            <IonCardSubtitle></IonCardSubtitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <IonText>
                                Connect Mobile App Installed:{" "}
                                {garminAppInstalled ? "Yes" : "No"}
                            </IonText>

                            <IonButton
                                onClick={() => {
                                    // Make garmin sdk prompt for install
                                    GarminSDK.initialiseGarminSdk()
                                        .then(() => {
                                            console.log(
                                                "[GarminDebugger] Try to initialise Garmin SDK"
                                            );
                                            openStoreForConnectMobile();
                                        })
                                        .catch((error) => {
                                            console.error(
                                                "[GarminDebugger] Error opening Garmin Connect Mobile App:",
                                                error
                                            );
                                        });
                                }}
                            >
                                Open Garmin Connect Mobile App
                            </IonButton>
                        </IonCardContent>
                    </IonCard>
                )}

                {garminAppInstalled && (
                    <>
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Device Management</IonCardTitle>
                                <IonCardSubtitle>
                                    {" "}
                                    Choose the watch device to communicate with
                                </IonCardSubtitle>
                            </IonCardHeader>
                            <IonCardContent>
                                {selectedDevice && (
                                    <>
                                        <pre>
                                            {JSON.stringify(
                                                selectedDevice,
                                                null,
                                                2
                                            )}
                                        </pre>
                                    </>
                                )}
                            </IonCardContent>
                            <IonCardContent>
                                <strong>Choose device: </strong>
                                <IonSelect
                                    value={selectedDevice?.uuid}
                                    placeholder="Select Device"
                                    onIonChange={(e) => {
                                        const selectedDeviceId = e.detail.value;
                                        const device =
                                            knownDevices &&
                                            knownDevices.find(
                                                (dev) =>
                                                    dev.uuid ===
                                                    selectedDeviceId
                                            );
                                        console.log("Selected device:", device);
                                        // Register for events on the selected device
                                        if (device) {
                                            setGarminSelectedDevice(device);
                                            // GarminSDK.registerForDeviceEvents({
                                            //     device: device,
                                            // })
                                            //     .then(() => {
                                            //         console.log(
                                            //             "Registered for device events on device:",
                                            //             device.uuid
                                            //         );
                                            //     })
                                            //     .catch((error) => {
                                            //         console.error(
                                            //             "Error registering for device events:",
                                            //             error
                                            //         );
                                            //     });
                                        }
                                    }}
                                >
                                    {knownDevices &&
                                        knownDevices.map((device) => (
                                            <IonSelectOption
                                                key={device.uuid}
                                                value={device.uuid}
                                            >
                                                {device.friendlyName}
                                            </IonSelectOption>
                                        ))}
                                </IonSelect>
                            </IonCardContent>
                        </IonCard>
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Actions</IonCardTitle>
                                <IonCardSubtitle>
                                    Choose the action to perform
                                </IonCardSubtitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <div>
                                    <label>Select Message:</label>
                                    <select
                                        value={selectedMessage}
                                        onChange={(e) =>
                                            setSelectedMessage(
                                                e.target.value as MessageKey
                                            )
                                        }
                                    >
                                        <option value="">
                                            --Select Message--
                                        </option>
                                        <option value="tokenResponse">
                                            Token Response
                                        </option>
                                        <option value="configurationResponse">
                                            Configuration Response
                                        </option>
                                        <option value="matchSummaryResponse">
                                            Match Summary Response
                                        </option>
                                        <option value="matchDetailsResponse">
                                            Match Details Response
                                        </option>
                                    </select>
                                </div>
                                <div>
                                    <label>Select Garmin App ID:</label>
                                    <select
                                        value={selectedAppId}
                                        onChange={handleAppIdChange}
                                    >
                                        <option
                                            value={GARMIN_FULL_APP_INFO.uuid}
                                        >
                                            Scorza
                                        </option>
                                        <option
                                            value={GARMIN_APP_IDS.REFSIX_LITE}
                                        >
                                            REFSIX Lite
                                        </option>
                                        <option
                                            value={
                                                GARMIN_APP_IDS.GARMIN_TEST_APP
                                            }
                                        >
                                            Garmin Test App
                                        </option>
                                    </select>
                                </div>
                                <IonButton
                                    onClick={sendMessage} // Ensuring no MouseEvent is passed
                                    disabled={
                                        !selectedDevice ||
                                        messageStatus === "Sending..."
                                    }
                                >
                                    {messageStatus === "Sending..."
                                        ? "Sending..."
                                        : selectedDevice
                                        ? `Send message to: ${selectedDevice.friendlyName}`
                                        : "Send message to Garmin watch"}
                                </IonButton>{" "}
                                <br />
                                <span>
                                    <strong>Message Status: </strong>
                                </span>{" "}
                                {messageStatus ? (
                                    <span>{messageStatus}</span>
                                ) : (
                                    <span>No status updates yet.</span>
                                )}{" "}
                                <br />
                                <IonButton
                                    onClick={getDeviceStatus} // Ensuring no MouseEvent is passed
                                    disabled={
                                        !selectedDevice ||
                                        deviceStatus === "Fetching status..."
                                    }
                                >
                                    {deviceStatus === "Fetching status..."
                                        ? "Fetching status..."
                                        : selectedDevice
                                        ? `Get status of: ${selectedDevice.friendlyName}`
                                        : "Get status of Garmin watch"}
                                </IonButton>{" "}
                                <br />
                                <span>
                                    <strong>Watch Status: </strong>
                                </span>{" "}
                                {deviceStatus ? (
                                    <pre>{deviceStatus}</pre>
                                ) : (
                                    <span>No status yet.</span>
                                )}{" "}
                                <br />
                                <IonButton onClick={checkAppInfo}>
                                    {" "}
                                    Check app info on watch{" "}
                                </IonButton>
                                <br />
                                <span>
                                    <strong>Application Status: </strong>
                                </span>{" "}
                                {buttonText ? (
                                    <pre>{buttonText}</pre>
                                ) : (
                                    <span>No app status yet.</span>
                                )}
                                <br />
                                <IonButton onClick={getKnownDevices}>
                                    Fetch updated list of devices
                                </IonButton>
                                <IonButton onClick={openGarminStore}>
                                    Open Garmin Store
                                </IonButton>
                                <IonButton onClick={openAppOnWatch}>
                                    Open App on Watch
                                </IonButton>
                                <br />
                                <span>
                                    <strong>App Open Status: </strong>
                                </span>{" "}
                                {appStatusMessage || "No status updates yet."}
                                <br />
                            </IonCardContent>
                        </IonCard>
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Message Listener</IonCardTitle>
                                <IonCardSubtitle>
                                    Messages received from watch
                                </IonCardSubtitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <pre>
                                    {JSON.stringify(messagesFromWatch, null, 4)}
                                </pre>
                            </IonCardContent>
                        </IonCard>
                    </>
                )}
            </IonContent>
        </IonPage>
    );
};
